import { UserRole, OrganizationRole } from "@/types/enums";
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '@/store';
import { updateOrganization } from '@/store/authSlice';
import { updateOrganizationSettings } from '@/store/organizationSlice';
import { timezoneService } from '@/services/timezoneService';
import { organizationSettingsService } from '@/services/organizationSettingsService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Toolbar,
  Autocomplete,
} from '@mui/material';
import {
  Save,
  PhotoCamera,
  Business,
  Settings as SettingsIcon,
  People,
  CreditCard,
  Security as SecurityIcon,

  Info as InfoIcon,
} from '@mui/icons-material';
import BillingDashboard from '@/components/BillingDashboard';
import UserManagement from '@/components/UserManagement';
import { useTabHistory } from '@/hooks/useTabHistory';
import { PreloadedAvatar } from '@/components/common/PreloadedAvatar';
import { getOrgPhotoUrl } from '@/utils/photoUtils';

const SettingsPage: React.FC = () => {
  const { user, token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'info';
  
  const { currentTab, changeTab } = useTabHistory({
    initialTab
  });
  
  // Get current organization data
  const currentOrg = user?.availableOrganizations?.find(
    org => org.organizationId === user?.currentOrganizationId
  );

  // Check permissions - redirect if user doesn't have admin access
  useEffect(() => {
    const hasPermission = user?.role === UserRole.SUPER_ADMIN || 
      (currentOrg && (currentOrg.role === OrganizationRole.OWNER || currentOrg.role === OrganizationRole.ADMIN));
    
    if (!hasPermission) {
      navigate('/dashboard');
    }
  }, [user, currentOrg, navigate]);
  
  // Load organization timezone
  useEffect(() => {
    const loadOrgTimezone = async () => {
      if (user?.currentOrganizationId) {
        try {
          const settings = await organizationSettingsService.getSettings(user.currentOrganizationId);
          setOrgTimezone(settings.timezone);
          timezoneService.setOrganizationTimezone(settings.timezone);
        } catch (error) {
          console.error('Failed to load organization timezone:', error);
          // Keep default timezone
        }
      }
    };
    
    loadOrgTimezone();
  }, [user?.currentOrganizationId]);
  
  const [orgName, setOrgName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [originalOrgName, setOriginalOrgName] = useState('');
  const [originalOrgEmail, setOriginalOrgEmail] = useState('');
  const [photoRefreshKey, setPhotoRefreshKey] = useState(Date.now());
  const [useGravatar, setUseGravatar] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // Timezone settings state
  const [orgTimezone, setOrgTimezone] = useState<string>('America/New_York');
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);
  const [timezoneSuccess, setTimezoneSuccess] = useState(false);

  // Compute org photo URL directly from user data — no API call needed
  const authenticatedOrgPhotoUrl = React.useMemo(() => {
    if (!user) return null;
    return getOrgPhotoUrl(user, 'large');
  }, [user, photoRefreshKey]);

  // Listen for photo updates
  useEffect(() => {
    const handlePhotoUpdate = () => setPhotoRefreshKey(Date.now());
    window.addEventListener('organizationPhotoUpdated', handlePhotoUpdate);
    return () => window.removeEventListener('organizationPhotoUpdated', handlePhotoUpdate);
  }, []);

  // Update state when user data loads
  useEffect(() => {
    console.log('User data:', user);
    console.log('Current org:', currentOrg);
    
    const loadOrganizationData = async () => {
      setLoading(true);
      
      if (currentOrg?.organizationName) {
        setOrgName(currentOrg.organizationName);
        setOriginalOrgName(currentOrg.organizationName);
      }
      
      // Load organization contact email, default to user email if not set
      if (user?.currentOrganizationId) {
        try {
          const orgSlug = (user as any)?.availableOrganizations?.find(
            (org: any) => org.organizationId === user.currentOrganizationId
          )?.organizationSlug;
          
          if (orgSlug) {
            const response = await fetch(`/api/v1/organizations/${orgSlug}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const orgData = await response.json();
              const contactEmail = orgData.data?.contactEmail || user.email;
              setOrgEmail(contactEmail);
              setOriginalOrgEmail(contactEmail);
            }
          }
        } catch (error) {
          console.error('Failed to load organization details:', error);
          // Fallback to user email
          if (user?.email) {
            setOrgEmail(user.email);
            setOriginalOrgEmail(user.email);
          }
        }
      }
      
      await loadPhotoConfig();
      setLoading(false);
    };
    
    if (user && currentOrg) {
      loadOrganizationData();
    }
  }, [currentOrg?.organizationName, user, token]);

  // Email validation helper
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getEmailError = () => {
    if (!showValidation) return "";
    if (!orgEmail) return "Contact email is required";
    if (!isValidEmail(orgEmail)) return "Please enter a valid email address";
    return "";
  };

  const handleOrgNameChange = (value: string) => {
    setOrgName(value);
    setHasChanges(true);
  };

  const handleOrgEmailChange = (value: string) => {
    setOrgEmail(value);
    setHasChanges(true);
  };

  const loadPhotoConfig = async () => {
    if (!user?.currentOrganizationId) return;
    try {
      const response = await fetch(`/api/v1/organizations/${user.currentOrganizationId}/profile-photo/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUseGravatar(data.type === 'GRAVATAR');
      }
    } catch (error) {
      console.error('Failed to load photo config:', error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      handleUploadPhoto(event.target.files[0]);
    }
  };

  const handleUploadPhoto = async (file: File) => {
    if (!user?.currentOrganizationId) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`/api/v1/organizations/${user.currentOrganizationId}/profile-photo/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setPhotoRefreshKey(Date.now());
        window.dispatchEvent(new Event('organizationPhotoUpdated'));
        toast.success('Organization photo updated');
      } else {
        const error = await response.json();
        toast.error(error.error?.message || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to update photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleGravatarToggle = async (enabled: boolean) => {
    if (!user?.currentOrganizationId) return;
    setUseGravatar(enabled);
    setUploadingPhoto(true);
    try {
      if (enabled) {
        const response = await fetch(`/api/v1/organizations/${user.currentOrganizationId}/profile-photo/gravatar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ gravatarEmail: orgEmail })
        });

        if (response.ok) {
          setPhotoRefreshKey(Date.now());
          window.dispatchEvent(new Event('organizationPhotoUpdated'));
        } else {
          const error = await response.json();
          toast.error(error.error?.message || 'Failed to enable Gravatar');
          setUseGravatar(false);
        }
      } else {
        const response = await fetch(`/api/v1/organizations/${user.currentOrganizationId}/profile-photo/upload-mode`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setPhotoRefreshKey(Date.now());
          window.dispatchEvent(new Event('organizationPhotoUpdated'));
        } else {
          const error = await response.json();
          toast.error(error.error?.message || 'No uploaded photo available');
          setUseGravatar(true);
        }
      }
    } catch (error) {
      console.error('Gravatar toggle error:', error);
      toast.error('Failed to update photo settings');
      setUseGravatar(!enabled);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveOrganization = async () => {
    if (!currentOrg || !user) return;
    
    // Show validation errors
    setShowValidation(true);
    
    // Check if email is valid before proceeding
    if (!orgEmail || !isValidEmail(orgEmail)) {
      return;
    }
    
    try {
      // Find the organization slug from available organizations
      const orgSlug = (user as any)?.availableOrganizations?.find(
        (org: any) => org.organizationId === user.currentOrganizationId
      )?.organizationSlug;
      
      if (!orgSlug) {
        console.error('Organization slug not found');
        return;
      }

      const response = await fetch(`/api/v1/organizations/${orgSlug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: orgName,
          contactEmail: orgEmail
        })
      });

      if (response.ok) {
        setHasChanges(false);
        setShowValidation(false);
        setOriginalOrgName(orgName);
        setOriginalOrgEmail(orgEmail);
        toast.success('Organization updated successfully');
        
        // Update Redux store to reflect the change in real-time
        if (user?.currentOrganizationId) {
          console.log('Dispatching updateOrganization:', {
            organizationId: user.currentOrganizationId,
            name: orgName
          });
          console.log('Current user data before update:', user);
          dispatch(updateOrganization({
            organizationId: user.currentOrganizationId,
            name: orgName
          }));
        }
        
        console.log('Organization updated successfully');
      } else {
        console.error('Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
    }
  };

  const handleCancelChanges = () => {
    setOrgName(originalOrgName);
    setOrgEmail(originalOrgEmail);
    setHasChanges(false);
  };

  const handleSaveTimezone = async (timezone: string) => {
    if (!user?.currentOrganizationId) return;

    setTimezoneLoading(true);
    setTimezoneError(null);
    setTimezoneSuccess(false);

    try {
      await organizationSettingsService.updateSettings(
        user.currentOrganizationId,
        { timezone }
      );
      
      // Update the timezone service with new org timezone
      timezoneService.setOrganizationTimezone(timezone);
      setOrgTimezone(timezone);
      
      // Update Redux store
      dispatch(updateOrganizationSettings({ timezone }));
      
      setTimezoneSuccess(true);
      setTimeout(() => setTimezoneSuccess(false), 3000);
    } catch (err: any) {
      setTimezoneError(err.response?.data?.message || 'Failed to update timezone');
    } finally {
      setTimezoneLoading(false);
    }
  };

  // Return null while loading - loading bar shows progress
  if (loading) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', mt: -8, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Vertical Sidebar Menu - Desktop only */}
      <Box sx={{ 
        display: { xs: 'none', md: 'block' },
        width: 240, 
        flexShrink: 0, 
        pr: 3, 
        pl: 3, 
        pt: 10 
      }}>
        <Box component="nav">
          <Typography variant="h6" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
            Organization
          </Typography>
          {([
            { tab: 'info', label: 'Information', icon: <InfoIcon />, match: (t: string) => t === 'info' || t === 'profile' },
            { tab: 'users', label: 'Users', icon: <People />, match: null },
            { tab: 'billing', label: 'Billing', icon: <CreditCard />, match: null },
            { tab: 'security', label: 'Security', icon: <SecurityIcon />, match: null },
          ]).map(({ tab, label, icon, match }) => (
            <Button
              key={tab}
              fullWidth
              variant="text"
              sx={{
                justifyContent: 'flex-start',
                pl: 2,
                mb: 0.5,
                ...((match ? match(currentTab) : currentTab === tab) && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }),
              }}
              onClick={() => changeTab(tab)}
              startIcon={icon}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        minWidth: 0,
        maxWidth: { xs: '100%', md: 800 },
        pl: { xs: 2, md: 3 },
        pr: { xs: 2, md: 3 },
        pt: { xs: 2, md: 10 },
        pb: { xs: 10, md: 3 }
      }}>
        {/* Spacer for mobile app bar */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Toolbar />
        </Box>

        {/* Settings Tab */}
        {(currentTab === 'info' || currentTab === 'profile') && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, minHeight: 40, display: 'flex', alignItems: 'center', mb: 1 }}>
              Contact Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage your organization's details, contact information, and branding.
            </Typography>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <PreloadedAvatar
                      src={authenticatedOrgPhotoUrl}
                      size={120}
                      fallbackIcon={<Business sx={{ fontSize: 80 }} />}
                      sx={{
                        cursor: useGravatar ? 'default' : 'pointer'
                      }}
                      onClick={() => !useGravatar && fileInputRef.current?.click()}
                    />
                    {!useGravatar && (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: '2px solid white',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            }
                          }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <PhotoCamera sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          style={{ display: 'none' }}
                          onChange={handleFileSelect}
                        />
                      </>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={useGravatar} 
                        onChange={(e) => handleGravatarToggle(e.target.checked)}
                        disabled={uploadingPhoto}
                      />
                    }
                    label="Use Gravatar"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Organization Name"
                    value={orgName}
                    onChange={(e) => handleOrgNameChange(e.target.value)}
                    fullWidth
                  />
                  
                  <TextField
                    label="Email"
                    value={orgEmail}
                    onChange={(e) => handleOrgEmailChange(e.target.value)}
                    fullWidth
                    type="email"
                    required
                    error={!!getEmailError()}
                    helperText={getEmailError()}
                  />

                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancelChanges}
                      disabled={!hasChanges}
                    >
                      Cancel
                    </Button>
                    <Button
                      startIcon={<Save />}
                      variant="contained"
                      onClick={handleSaveOrganization}
                      disabled={!hasChanges}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Regional Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={timezoneService.getCommonTimezones()}
                  value={timezoneService.getCommonTimezones().find(tz => tz.value === orgTimezone) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setOrgTimezone(newValue.value);
                      handleSaveTimezone(newValue.value);
                    }
                  }}
                  getOptionLabel={(option) => option.label}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.offset}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organization Timezone"
                      helperText="Default timezone for all users in this organization"
                      fullWidth
                      disabled={timezoneLoading}
                    />
                  )}
                />

                {timezoneError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {timezoneError}
                  </Alert>
                )}

                {timezoneSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Timezone updated successfully
                  </Alert>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
          </>
      )}

      {/* Users Tab */}
      {currentTab === 'users' && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, minHeight: 40, display: 'flex', alignItems: 'center', mb: 1 }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Invite and manage users, assign roles, and control access to your organization.
          </Typography>
          <UserManagement />
        </>
      )}

      {/* Billing Tab */}
      {currentTab === 'billing' && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, minHeight: 40, display: 'flex', alignItems: 'center', mb: 1 }}>
            Billing & Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your subscription plan, payment methods, and billing history.
          </Typography>
          <BillingDashboard
            orgSlug={(user as any)?.availableOrganizations?.find(
              (org: any) => org.organizationId === user?.currentOrganizationId
            )?.organizationSlug || ''}
            isOwner={currentOrg?.role === OrganizationRole.OWNER || user?.role === UserRole.SUPER_ADMIN}
          />
        </>
      )}

      {/* Security Tab */}
      {currentTab === 'security' && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, minHeight: 40, display: 'flex', alignItems: 'center', mb: 1 }}>
            Security Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage security policies, authentication methods, and access controls.
          </Typography>
          <Card>
            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                Security settings will be available soon.
              </Alert>
            </CardContent>
          </Card>
        </>
      )}
      </Box>

      {/* Bottom Navigation - Mobile only */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', md: 'none' },
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        elevation={3}
      >
        <BottomNavigation
          value={currentTab}
          onChange={(_, newValue) => {
            changeTab(newValue);
          }}
          showLabels
        >
          <BottomNavigationAction label="Settings" value="info" icon={<SettingsIcon />} />
          <BottomNavigationAction label="Users" value="users" icon={<People />} />
          <BottomNavigationAction label="Billing" value="billing" icon={<CreditCard />} />
          <BottomNavigationAction label="Security" value="security" icon={<SecurityIcon />} />
        </BottomNavigation>
      </Paper>

    </Box>
  );
};

export default SettingsPage;
